import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import logo from '../assets/logo-centrao.png';
import { PUBLICO_BASE_URL } from '../config';

// Pagina PUBLICA (sem login) — wrapper visual em volta do PDF servido pelo
// sistema-suc na rota /publico/{tipo}/:id.pdf?exp=&sig= (HMAC).
//
// Layout responsivo:
//   • Desktop / Android: iframe ocupando a area abaixo do header. Funciona bem
//     porque ambos tem renderer de PDF embutido (PDFium/Chromium).
//   • iOS Safari: NAO embute PDF em iframe — mostraria uma pagina em branco.
//     Detectamos iOS e renderizamos um card centralizado com botao grande
//     "Abrir PDF" que invoca o visualizador nativo do sistema.

function detectarIOS() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  // iPad moderno se identifica como Mac com touch — testar maxTouchPoints tambem.
  const iPadOSModerno = /Macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
  return /iPad|iPhone|iPod/.test(ua) || iPadOSModerno;
}

export default function VerOrcamento({ tipo }) {
  const { id: idRaw } = useParams();
  // O id pode chegar como "9497" (rota /p/...) ou "9497.pdf" (rota canonica
  // /publico/{tipo}/:id.pdf que o sistema-suc gera). Normaliza tirando o sufixo.
  const id = (idRaw || '').replace(/\.pdf$/i, '');

  const [params] = useSearchParams();
  const exp = params.get('exp');
  const sig = params.get('sig');

  const pdfUrl = useMemo(() => {
    const base = (PUBLICO_BASE_URL || '').replace(/\/+$/, '');
    const qs = new URLSearchParams();
    if (exp) qs.set('exp', exp);
    if (sig) qs.set('sig', sig);
    return `${base}/publico/${tipo}/${encodeURIComponent(id)}.pdf?${qs.toString()}`;
  }, [tipo, id, exp, sig]);

  const titulo = tipo === 'cotacao' ? 'Cotação' : 'Orçamento';
  const linkInvalido = !exp || !sig;
  const expirou = !linkInvalido && Number(exp) > 0 && Number(exp) < Math.floor(Date.now() / 1000);
  const [iframeErro, setIframeErro] = useState(false);

  // Detecta iOS uma vez no mount. Memoizamos no estado para evitar recalculo
  // durante re-renders e tambem para nao quebrar SSR (typeof navigator).
  const [ehIOS, setEhIOS] = useState(false);
  useEffect(() => { setEhIOS(detectarIOS()); }, []);

  let erro = null;
  if (linkInvalido) {
    erro = { codigo: 'sem_assinatura', mensagem: 'Link sem assinatura válida. Solicite um novo link ao vendedor.' };
  } else if (expirou) {
    erro = { codigo: 'expirado', mensagem: 'Este link expirou. Solicite um novo link ao vendedor para visualizar o documento.' };
  } else if (iframeErro) {
    erro = { codigo: 'falha', mensagem: 'Não foi possível abrir o documento. Tente novamente em alguns instantes ou solicite um novo link.' };
  }

  return (
    <div className="publico-wrap-cli">
      <header className="publico-header-cli">
        <div className="publico-header-inner-cli">
          <img src={logo} alt="Centrão Materiais" className="publico-logo-cli" />
          <div className="publico-header-texto-cli">
            <h1 className="publico-titulo-cli">{titulo} #{id}</h1>
            <p className="publico-subtitulo-cli">Centrão Materiais de Construção</p>
          </div>
        </div>
        {!erro && (
          <div className="publico-acoes-cli">
            <a
              className="publico-acao-btn-cli publico-acao-secundaria-cli"
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Abrir PDF em nova aba"
            >
              <i className="bi bi-box-arrow-up-right" />
              <span className="publico-acao-label-cli">Abrir</span>
            </a>
            <a
              className="publico-acao-btn-cli publico-acao-primaria-cli"
              href={pdfUrl}
              download={`${tipo}-${id}.pdf`}
              aria-label="Baixar PDF"
            >
              <i className="bi bi-download" />
              <span className="publico-acao-label-cli">Baixar</span>
            </a>
          </div>
        )}
      </header>

      <main className="publico-main-cli">
        {erro ? (
          <div className="publico-erro-cli card-cli">
            <i className={`bi ${erro.codigo === 'expirado' ? 'bi-clock-history' : 'bi-exclamation-octagon'} publico-erro-icone-cli`} />
            <h2>Link indisponível</h2>
            <p>{erro.mensagem}</p>
            <small>Código: {erro.codigo}</small>
          </div>
        ) : ehIOS ? (
          // iOS Safari nao embute PDF — entrega uma tela centrada com botao
          // grande. Tap no botao abre o visualizador de PDF nativo do iOS.
          <div className="publico-ios-cli">
            <div className="publico-ios-icone-cli">
              <i className="bi bi-file-earmark-pdf" />
            </div>
            <h2>Toque para visualizar</h2>
            <p>Para abrir o {titulo.toLowerCase()} no seu iPhone ou iPad, toque no botão abaixo.</p>
            <a
              className="publico-ios-btn-cli"
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="bi bi-eye" /> Visualizar {titulo}
            </a>
            <a
              className="publico-ios-link-cli"
              href={pdfUrl}
              download={`${tipo}-${id}.pdf`}
            >
              <i className="bi bi-download" /> Baixar para o dispositivo
            </a>
          </div>
        ) : (
          <div className="publico-pdf-frame-cli">
            <iframe
              title={`${titulo} ${id}`}
              src={pdfUrl}
              onError={() => setIframeErro(true)}
            />
          </div>
        )}
      </main>

      <footer className="publico-footer-cli">
        <span>© Centrão Materiais de Construção</span>
        <span className="publico-footer-secundario-cli">Documento gerado eletronicamente.</span>
      </footer>
    </div>
  );
}
